import type { ComponentType, ReactNode } from 'react';
import { Component } from 'react';

import { IS_DEBUG } from '@utilities/browser';
import { Warn } from '@utilities/logging';

export interface ErrorBoundaryProps {
  children: ReactNode;
  onComponentError?: (error: Error, info: any) => unknown;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
  }
  state = { hasError: false };

  componentDidCatch(error: Error, info: any) {
    const { onComponentError } = this.props;

    if (onComponentError && typeof onComponentError === 'function') onComponentError(error, info);

    // Display fallback UI
    this.setState({ hasError: true });
    // You can also log the error to an error reporting service
    Warn(error, info);
  }

  render() {
    const { hasError } = this.state;

    if (hasError) return <>{IS_DEBUG && <h1>Something went wrong.</h1>}</>;

    return this.props.children;
  }
}

export function withErrorBoundary<P>(BaseComponent: ComponentType<P>, errorBoundaryProps?: ErrorBoundaryProps) {
  const Wrapped = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <BaseComponent {...props} ref={null} />
    </ErrorBoundary>
  );

  const name = BaseComponent.displayName || BaseComponent.name || 'Unknown';
  Wrapped.displayName = `withErrorBoundary(${name})`;

  return Wrapped;
}
